import React, {useState, useContext, useEffect } from "react";
import { Redirect } from "react-router-dom";
import { BreadcrumbContext } from '../../../../components/Breadcrumb';
import { FirebaseAuth } from "../../../../components/FirebaseAuth/firebase";
import { Box, Button, Chip, Grid, Paper, Stack, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import OnboardingContractor from "../../contractor/Onboarding";
import OnboardingClient from "../../client/Onboarding";
import Loader from "../../../../components/Loader";
import brand from "../../../../inc/branding.json";

const ContractorRoleIllustration = () => (
    <Box component="svg" viewBox="0 0 160 120" aria-hidden="true" sx={{ width: 150, maxWidth: "100%" }}>
        <defs>
            <linearGradient id="contractor-role-bg" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0%" stopColor="#E7F6EC" />
                <stop offset="100%" stopColor="#CDEDD8" />
            </linearGradient>
        </defs>
        <rect x="12" y="10" width="136" height="96" rx="24" fill="url(#contractor-role-bg)" />
        <circle cx="52" cy="43" r="16" fill="#05690D" opacity="0.95" />
        <path d="M25 86c3.5-17.2 17-27 31-27s27.5 9.8 31 27" fill="#05690D" opacity="0.95" />
        <rect x="79" y="37" width="48" height="34" rx="8" fill="#FFFFFF" stroke="#98D8A9" strokeWidth="3" />
        <path d="M89 51h28M89 61h18" stroke="#05690D" strokeWidth="5" strokeLinecap="round" />
        <path d="M69 93h64" stroke="#05690D" strokeWidth="8" strokeLinecap="round" opacity="0.78" />
        <circle cx="121" cy="31" r="7" fill="#05690D" />
        <path d="M121 18v6M121 38v6M108 31h6M128 31h6M112 22l4 4M126 36l4 4M130 22l-4 4M116 36l-4 4" stroke="#05690D" strokeWidth="3" strokeLinecap="round" />
    </Box>
);

const ClientRoleIllustration = () => (
    <Box component="svg" viewBox="0 0 160 120" aria-hidden="true" sx={{ width: 150, maxWidth: "100%" }}>
        <defs>
            <linearGradient id="client-role-bg" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0%" stopColor="#E7F5FD" />
                <stop offset="100%" stopColor="#CFE9F8" />
            </linearGradient>
        </defs>
        <rect x="12" y="10" width="136" height="96" rx="24" fill="url(#client-role-bg)" />
        <rect x="34" y="38" width="42" height="42" rx="11" fill="#FFFFFF" stroke="#A7D8F2" strokeWidth="3" />
        <rect x="84" y="38" width="42" height="42" rx="11" fill="#FFFFFF" stroke="#A7D8F2" strokeWidth="3" />
        <path d="M67 60h26" stroke="#01579B" strokeWidth="6" strokeLinecap="round" />
        <path d="M52 58l11 11 21-24" fill="none" stroke="#01579B" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M98 54h14M98 65h18" stroke="#01579B" strokeWidth="5" strokeLinecap="round" />
        <circle cx="80" cy="91" r="9" fill="#01579B" opacity="0.9" />
        <path d="M55 91h50" stroke="#01579B" strokeWidth="5" strokeLinecap="round" opacity="0.5" />
        <path d="M80 82V70" stroke="#01579B" strokeWidth="5" strokeLinecap="round" opacity="0.5" />
    </Box>
);

const roleCards = {
    contractor: {
        title: "Contractor",
        eyebrow: "I deliver work",
        description: "Track work, submit timesheets, manage invoices, and receive payouts through approved engagements.",
        accent: "#05690D",
        softBg: "rgba(5,105,13,0.08)",
        border: "rgba(5,105,13,0.24)",
        Illustration: ContractorRoleIllustration,
        points: ["Timesheets", "Invoices", "Payouts"],
    },
    client: {
        title: "Client",
        eyebrow: "I hire talent",
        description: "Request hires, review contract progress, approve work, and monitor account spending and billing.",
        accent: "#01579B",
        softBg: "rgba(2,136,209,0.08)",
        border: "rgba(2,136,209,0.24)",
        Illustration: ClientRoleIllustration,
        points: ["Hire requests", "Contracts", "Billing"],
    },
};

const Home = () => {    
    const title = 'Home';
    const dba = brand.brandConfig?.config?.dba;
    const [loading, setLoading] = useState(true);
    const [contractorReady, setContractorReady] = useState(false);
    const [clientReady, setClientReady] = useState(false);
    const [role, setRole] = useState(null);
    const [savingRole, setSavingRole] = useState(false);
    const [allowedRoles, setAllowedRoles] = useState(["contractor", "client"]);
    const [roleSelectionError, setRoleSelectionError] = useState("");

    const { setBreadcrumb } = useContext(BreadcrumbContext);

    const getUserdata = async () => {
        setLoading(true);
        setRoleSelectionError("");
        const uid = FirebaseAuth.auth().currentUser?.uid;
        if (!uid) {
            setContractorReady(false);
            setClientReady(false);
            setRole(null);
            setLoading(false);
            return;
        }
        try {
            const getAllowedRoleOptions = FirebaseAuth.functions().httpsCallable("getAllowedRoleOptions");
            const resp = await getAllowedRoleOptions({});
            const roles = Array.isArray(resp?.data?.allowedRoles) ? resp.data.allowedRoles : ["contractor", "client"];
            setAllowedRoles(roles.length ? roles : ["contractor", "client"]);
        } catch (_error) {
            setAllowedRoles(["contractor", "client"]);
        }
        const userRef = FirebaseAuth.firestore().collection('users').doc(uid);
        const userDoc = await userRef.get();
        if (userDoc.exists) {
            const data = userDoc.data() || {};
            const hasConnectAccount = !!String(data.id || "").trim();
            const hasActiveClientAccount = !!String(data.active_account_id || "").trim();
            const hasClientAccountIds = Array.isArray(data.account_ids) && data.account_ids.length > 0;
            const hasCustomerAccount = hasActiveClientAccount && hasClientAccountIds;
            const resolvedRole = data.role || (hasConnectAccount ? 'contractor' : (hasCustomerAccount ? 'client' : null));
            setRole(resolvedRole);
            setContractorReady(hasConnectAccount);
            setClientReady(hasCustomerAccount);

            // Existing users are contractors by default.
            if (!data.role && hasConnectAccount) {
                try {
                    const selectUserRole = FirebaseAuth.functions().httpsCallable("selectUserRole");
                    await selectUserRole({ role: "contractor" });
                } catch (_error) {
                    // fallback to existing resolved role display
                }
            }
        } else {
            setRole(null);
            setContractorReady(false);
            setClientReady(false);
        }
        setLoading(false);
    }

    const handleRoleSelect = async (nextRole) => {
        const uid = FirebaseAuth.auth().currentUser?.uid;
        if (!uid || !nextRole) return;
        if (!allowedRoles.includes(nextRole)) {
            setRoleSelectionError("You are not allowed to select this role.");
            return;
        }

        setSavingRole(true);
        setRoleSelectionError("");
        try {
            const selectUserRole = FirebaseAuth.functions().httpsCallable("selectUserRole");
            const res = await selectUserRole({ role: nextRole });
            const resolved = res?.data?.role || nextRole;
            try {
                await FirebaseAuth.auth().currentUser?.getIdToken(true);
            } catch (_tokenError) {
                // token refresh best-effort
            }
            setRole(resolved);
        } catch (error) {
            const msg = String(error?.message || "Unable to assign role.");
            setRoleSelectionError(msg.replace(/^Firebase:\s*/i, "").trim());
        } finally {
            setSavingRole(false);
        }
    };

    useEffect(() => {
        setBreadcrumb([
            {
                to: "/",
                text: "Home",
                active: false
            },
            {
                to: null,
                text: title,
                active: true
            }
        ]);
        getUserdata();
    }, [setBreadcrumb, title]);

    return (
        <>
            {(loading) ? (
                <Loader variant="fullscreen" open={loading} label="Loading dashboard..." />
            ):( 
                <>
                    {!role && (
                        <Box
                          sx={{
                            p: { xs: 2, sm: 3 },
                            minHeight: "calc(100vh - 210px)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background:
                              "radial-gradient(circle at top left, rgba(25,118,210,0.08), transparent 34%), radial-gradient(circle at bottom right, rgba(5,105,13,0.08), transparent 30%)",
                          }}
                        >
                            <Paper
                              variant="outlined"
                              sx={{
                                p: { xs: 2.25, sm: 3.5, md: 4 },
                                borderRadius: 4,
                                maxWidth: 1040,
                                width: "100%",
                                mx: "auto",
                                textAlign: "center",
                                borderColor: "rgba(218,220,224,0.9)",
                                boxShadow: "0 18px 60px rgba(60,64,67,0.08)",
                                overflow: "hidden",
                                position: "relative",
                                "&:before": {
                                  content: '""',
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  height: 5,
                                  background: "linear-gradient(90deg, #05690D, #1976D2)",
                                },
                              }}
                            >
                                <Chip
                                  label="Account setup"
                                  size="small"
                                  sx={{
                                    mb: 1.5,
                                    borderColor: "#d8dee7",
                                    bgcolor: "#f8fafd",
                                    fontWeight: 700,
                                    letterSpacing: 0.2,
                                  }}
                                  variant="outlined"
                                />
                                <Typography variant="h4" sx={{ fontWeight: 850, mb: 1, color: "#30343b", letterSpacing: "-0.03em" }}>
                                    Choose your role
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ mb: { xs: 3, sm: 4 }, maxWidth: 560, mx: "auto" }}>
                                    Pick the workspace that matches how you will use {dba}. We will tailor your dashboard, onboarding, and billing flow from here.
                                </Typography>

                                <Grid container spacing={2.5} justifyContent="center" alignItems="stretch">
                                    {["contractor", "client"].map((roleKey) => {
                                      if (!allowedRoles.includes(roleKey)) return null;
                                      const card = roleCards[roleKey];
                                      const Illustration = card.Illustration;
                                      return (
                                        <Grid item xs={12} md={6} key={roleKey} sx={{ display: "flex" }}>
                                            <Button
                                              variant="outlined"
                                              disabled={savingRole}
                                              onClick={() => handleRoleSelect(roleKey)}
                                              fullWidth
                                              sx={{
                                                height: "100%",
                                                textTransform: "none",
                                                borderRadius: 4,
                                                px: { xs: 2, sm: 2.5 },
                                                py: { xs: 2.25, sm: 3 },
                                                minHeight: { xs: 420, md: 430 },
                                                display: "flex",
                                                alignItems: "stretch",
                                                borderColor: "#d9dde3",
                                                color: "text.primary",
                                                bgcolor: "#fff",
                                                position: "relative",
                                                overflow: "hidden",
                                                transition: "transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease, background-color 180ms ease",
                                                "&:before": {
                                                  content: '""',
                                                  position: "absolute",
                                                  inset: 0,
                                                  background: `linear-gradient(145deg, ${card.softBg}, transparent 52%)`,
                                                  opacity: 0.9,
                                                },
                                                "&:hover": {
                                                  transform: "translateY(-4px)",
                                                  borderColor: card.border,
                                                  boxShadow: "0 18px 38px rgba(60,64,67,0.13)",
                                                  bgcolor: "#fff",
                                                },
                                                "&:focus-visible": {
                                                  outline: `3px solid ${card.border}`,
                                                  outlineOffset: 3,
                                                },
                                              }}
                                            >
                                                <Stack
                                                  spacing={2}
                                                  sx={{ width: "100%", minHeight: "100%", position: "relative", zIndex: 1 }}
                                                  alignItems="center"
                                                >
                                                    <Box sx={{ width: "100%", display: "flex", justifyContent: "center", minHeight: 132, alignItems: "center" }}>
                                                        <Illustration />
                                                    </Box>
                                                    <Box sx={{ minHeight: 74 }}>
                                                        <Typography variant="caption" sx={{ color: card.accent, fontWeight: 850, letterSpacing: 0.8, textTransform: "uppercase" }}>
                                                            {card.eyebrow}
                                                        </Typography>
                                                        <Typography variant="h5" sx={{ fontWeight: 850, mt: 0.5, color: "#30343b" }}>
                                                            {card.title}
                                                        </Typography>
                                                    </Box>
                                                    <Typography
                                                      variant="body1"
                                                      color="text.secondary"
                                                      sx={{ textAlign: "center", maxWidth: 360, lineHeight: 1.55, minHeight: { md: 75 } }}
                                                    >
                                                        {card.description}
                                                    </Typography>
                                                    <Stack
                                                      direction="row"
                                                      spacing={1}
                                                      useFlexGap
                                                      flexWrap="wrap"
                                                      justifyContent="center"
                                                      alignItems="center"
                                                      sx={{ minHeight: 32 }}
                                                    >
                                                        {card.points.map((point) => (
                                                            <Chip
                                                              key={point}
                                                              size="small"
                                                              label={point}
                                                              sx={{
                                                                bgcolor: card.softBg,
                                                                color: card.accent,
                                                                borderColor: card.border,
                                                                fontWeight: 700,
                                                              }}
                                                              variant="outlined"
                                                            />
                                                        ))}
                                                    </Stack>
                                                    <Box sx={{ mt: "auto", display: "inline-flex", alignItems: "center", gap: 0.75, color: card.accent, fontWeight: 850 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 850 }}>
                                                            Continue as {card.title}
                                                        </Typography>
                                                        <ArrowForwardIcon fontSize="small" />
                                                    </Box>
                                                </Stack>
                                            </Button>
                                        </Grid>
                                      );
                                    })}
                                </Grid>

                                {!!roleSelectionError && (
                                  <Typography variant="caption" color="error" sx={{ display: "block", mt: 2 }}>
                                      {roleSelectionError}
                                  </Typography>
                                )}

                                <Box
                                  sx={{
                                    mt: { xs: 2.5, sm: 3.5 },
                                    mx: "auto",
                                    maxWidth: 820,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 1,
                                    px: 2,
                                    py: 1.25,
                                    borderRadius: 3,
                                    bgcolor: "#f8fafd",
                                    border: "1px solid #e3e7ee",
                                  }}
                                >
                                    <LockOutlinedIcon sx={{ fontSize: 18, color: "text.secondary", flexShrink: 0 }} />
                                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: "left" }}>
                                        Role selection is permanent for this account.
                                    </Typography>
                                </Box>
                            </Paper>
                        </Box>
                    )}

                    {role === 'contractor' && contractorReady && <Redirect to="/contractor/dashboard" />}
                    {role === 'contractor' && !contractorReady && <OnboardingContractor />}

                    {role === 'client' && clientReady && <Redirect to="/client/dashboard" />}
                    {role === 'client' && !clientReady && <OnboardingClient />}

                    {role === 'admin' && <Redirect to="/admin/dashboard" />}
                </>
            )}
        </>
    )
}

export default Home;
